import { useEffect, useState, type JSX } from 'react'
import { ErrorBoundary } from './lib/ErrorBoundary.js'
import { useRoute } from './lib/router.js'
import { useSession } from './lib/session.js'
import { Arena } from './pages/Arena.js'
import { EngineGame } from './pages/EngineGame.js'
import { Home } from './pages/Home.js'
import { LocalGame } from './pages/LocalGame.js'
import { MyGames } from './pages/MyGames.js'
import { Queue } from './pages/Queue.js'
import { RemoteGame } from './pages/RemoteGame.js'
import { useKeyboardInset } from './lib/useKeyboardInset.js'
import { Settings } from './theme/Settings.js'
import { usePrefs } from './theme/usePrefs.js'
import { unlockAudio, watchAudioLifecycle } from './sound/engine.js'

export const App = (): JSX.Element => {
  const { route, navigate } = useRoute()
  const session = useSession()
  const { prefs, setPrefs } = usePrefs(session.user !== null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  useKeyboardInset()

  useEffect(() => {
    // Browsers will not start an AudioContext until the user has interacted, so
    // the very first press anywhere primes it.
    const prime = (): void => unlockAudio()
    window.addEventListener('pointerdown', prime, { once: true })
    // ...and iOS suspends it again on every trip to the background, which is
    // why sounds used to go silent after switching apps.
    const stopWatching = watchAudioLifecycle()
    return () => {
      window.removeEventListener('pointerdown', prime)
      stopWatching()
    }
  }, [])

  return (
    <ErrorBoundary>
      {route.name === 'home' && (
        <Home navigate={navigate} session={session} onSettings={() => setSettingsOpen(true)} />
      )}
      {route.name === 'local' && <LocalGame navigate={navigate} />}
      {route.name === 'engine' && (
        <EngineGame
          timeControlId={route.timeControlId}
          modelId={route.modelId}
          navigate={navigate}
          onSettings={() => setSettingsOpen(true)}
        />
      )}
      {route.name === 'queue' && (
        <Queue timeControlId={route.timeControlId} navigate={navigate} />
      )}
      {route.name === 'me' && <MyGames session={session} navigate={navigate} />}
      {route.name === 'arena' && <Arena session={session} navigate={navigate} />}
      {route.name === 'room' && (
        <RemoteGame
          roomId={route.roomId}
          playerKey={route.playerKey}
          navigate={navigate}
          onSettings={() => setSettingsOpen(true)}
        />
      )}

      {settingsOpen && (
        <Settings
          prefs={prefs}
          onChange={setPrefs}
          onClose={() => setSettingsOpen(false)}
          signedIn={session.user !== null}
        />
      )}
    </ErrorBoundary>
  )
}
